'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { ArrowRight, MapPin, TrendingUp } from 'lucide-react';
import { getMainCategories, getCategoryListingCounts, getCategoryImage, getCategoryColor, getCategoryTag } from '@/lib/services/categoriesService';

interface CategoryDisplay {
  id: string;
  label: string;
  count: string;
  href: string;
  image: string;
  imageAlt: string;
  tag: string;
  tagColor: string;
  size: 'large' | 'small';
}

function SkeletonCard({ large }: { large?: boolean }) {
  return (
    <div
      className="category-card animate-pulse bg-[#E2E2E7]"
      style={{ height: large ? '480px' : '224px' }}
    />
  );
}

export default function CategoryGrid() {
  const [categories, setCategories] = useState<CategoryDisplay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [cats, counts] = await Promise.all([
        getMainCategories(10),
        getCategoryListingCounts(),
      ]);

      const mapped: CategoryDisplay[] = cats.map((cat, i) => {
        const cnt = counts[cat.id] || 0;
        const countLabel = cnt > 0 ? `${cnt.toLocaleString('en-IN')}+ listings` : 'New Category';
        return {
          id: cat.id,
          label: cat.name,
          count: countLabel,
          href: `/listings-page?category=${cat.slug}`,
          image: getCategoryImage(cat),
          imageAlt: `${cat.name} listings on NammaPrice`,
          tag: getCategoryTag(cat),
          tagColor: getCategoryColor(cat),
          size: i === 0 ? 'large' : 'small',
        };
      });

      setCategories(mapped);
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <section className="section-bg-light py-12 lg:py-20">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="flex items-end justify-between mb-8 lg:mb-12">
            <div>
              <p className="section-label mb-2.5">Browse by Type</p>
              <h2 className="section-heading">Discover Different<br />Lifestyles</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-12 gap-3 lg:gap-4">
            <div className="col-span-2 lg:col-span-5 lg:row-span-2">
              <SkeletonCard large />
            </div>
            <div className="col-span-2 lg:col-span-7 grid grid-cols-2 gap-3 lg:gap-4">
              {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) return null;

  return (
    <section className="section-bg-light py-12 lg:py-20">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between mb-8 lg:mb-12">
          <div>
            <p className="section-label mb-2.5">Browse by Type</p>
            <h2 className="section-heading text-[24px] sm:text-[32px] lg:text-[40px]">Discover Different<br />Lifestyles</h2>
          </div>
          <Link
            href="/listings-page"
            className="hidden md:flex items-center gap-2 text-[13px] font-600 text-[#D93025] hover:text-[#B71C1C] transition-colors group">
            View All Categories
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Mobile: horizontal scroll strip for categories */}
        <div className="lg:hidden flex gap-3 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-2" style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
          {categories.map((cat) => (
            <Link
              key={cat.id}
              href={cat.href}
              className="category-card block group flex-shrink-0 touch-manipulation active:scale-95 transition-transform"
              style={{ width: '160px', height: '180px', scrollSnapAlign: 'start' }}>
              <div className="relative w-full h-full">
                <AppImage
                  src={cat.image}
                  alt={cat.imageAlt}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                <div className="absolute inset-0" style={{
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.72) 100%)'
                }} />
                <div className="absolute top-2 left-2">
                  <span className="text-white text-[9px] font-700 px-2 py-0.5 rounded-full"
                    style={{ background: cat.tagColor, letterSpacing: '0.04em' }}>
                    {cat.tag}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white/55 text-[10px] font-500 mb-0.5">{cat.count}</p>
                  <h3 className="text-white font-600" style={{ fontSize: '13px', letterSpacing: '-0.01em' }}>
                    {cat.label}
                  </h3>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Desktop: original grid layout */}
        <div className="hidden lg:grid grid-cols-12 gap-4">
          <Link
            href={categories[0]?.href}
            className="category-card block group col-span-5 row-span-2"
            style={{ height: '480px' }}>
            <div className="relative w-full h-full">
              <AppImage
                src={categories[0]?.image}
                alt={categories[0]?.imageAlt}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              <div className="absolute inset-0" style={{
                background: 'linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 40%, rgba(0,0,0,0.78) 100%)'
              }} />
              <div className="absolute top-4 left-4">
                <span className="inline-flex items-center gap-1.5 text-white text-[11px] font-700 px-3 py-1.5 rounded-full"
                  style={{ background: categories[0]?.tagColor, letterSpacing: '0.04em' }}>
                  <TrendingUp size={10} />
                  {categories[0]?.tag}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-white/60 text-[12px] font-500 mb-1.5 flex items-center gap-1">
                  <MapPin size={10} />
                  {categories[0]?.count}
                </p>
                <div className="flex items-end justify-between">
                  <h3 className="text-white font-700 leading-tight" style={{ fontSize: '26px', letterSpacing: '-0.02em' }}>
                    {categories[0]?.label}
                  </h3>
                  <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center group-hover:bg-[#D93025] group-hover:border-[#D93025] transition-all duration-300">
                    <ArrowRight size={15} className="text-white group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          </Link>

          <div className="col-span-7 grid grid-cols-2 gap-4">
            {categories.slice(1, 5).map((cat) => (
              <Link
                key={cat.id}
                href={cat.href}
                className="category-card block group"
                style={{ height: '224px' }}>
                <div className="relative w-full h-full">
                  <AppImage
                    src={cat.image}
                    alt={cat.imageAlt}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                  <div className="absolute inset-0" style={{
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.72) 100%)'
                  }} />
                  <div className="absolute top-3 left-3">
                    <span className="text-white text-[10px] font-700 px-2.5 py-1 rounded-full"
                      style={{ background: cat.tagColor, letterSpacing: '0.04em' }}>
                      {cat.tag}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white/55 text-[11px] font-500 mb-1">{cat.count}</p>
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-600" style={{ fontSize: '16px', letterSpacing: '-0.02em' }}>
                        {cat.label}
                      </h3>
                      <ArrowRight size={13} className="text-white/60 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-5 md:hidden">
          <Link
            href="/listings-page"
            className="flex items-center justify-center gap-2 w-full py-3 border border-[#E2E2E7] rounded-[12px] text-[13px] font-600 text-[#D93025] hover:bg-[#FFEBEE] transition-colors touch-manipulation">
            View All Categories
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}